import React, { useState, useEffect } from 'react';
import type { FormEvent, ChangeEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faArrowLeft, faCalendarAlt, faUser, faPlus, faTimes, faEnvelope, faKey, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { blogPostsData } from '../data/blogData';
import ResetPassword from '../components/ResetPassword';
import { login, checkServerStatus } from '../services/authService';

// Define API URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Animation variants with improved smoothness
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.4, ease: "easeOut" } }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 15 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.5, 
      ease: [0.25, 0.1, 0.25, 1] // cubic-bezier for smoother motion
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  image: string;
  date: string;
  author: string;
  category: string;
  tags: string[];
  content: string[];
}

const Blog: React.FC = () => {
  // State management
  const [currentSlide, setCurrentSlide] = useState(0);
  const [visiblePosts, setVisiblePosts] = useState(6);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [adminUsername, setAdminUsername] = useState("admin");
  const [token, setToken] = useState("");
  const [showNewPostForm, setShowNewPostForm] = useState(false);
  const [showForgotPasswordForm, setShowForgotPasswordForm] = useState(false);
  const [resetEmail, setResetEmail] = useState("jangradinesh8696@gmail.com");
  const [resetSuccess, setResetSuccess] = useState(false);
  const [newPost, setNewPost] = useState<BlogPost>({
    id: blogPostsData.length + 1,
    title: "",
    excerpt: "",
    image: "https://picsum.photos/id/28/800/500",
    date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    author: "Admin",
    category: "Career Advice",
    tags: ["New", "Recent"],
    content: ["Write your content here..."]
  });
  const [localBlogPosts, setLocalBlogPosts] = useState<BlogPost[]>([...blogPostsData]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [serverConnected, setServerConnected] = useState<boolean | null>(null);

  // Load blog posts from API on component mount
  React.useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_URL}/blog-posts`);
        
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setLocalBlogPosts(data);
          } else {
            // Fallback to local data if API returns empty
            const savedPosts = localStorage.getItem('blogPosts');
            if (savedPosts) {
              setLocalBlogPosts(JSON.parse(savedPosts));
            }
          }
        } else {
          throw new Error('Failed to fetch blog posts');
        }
      } catch (err) {
        console.error("Error fetching blog posts:", err);
        setError("Failed to load blog posts");
        // Fallback to local data
        const savedPosts = localStorage.getItem('blogPosts');
        if (savedPosts) {
          setLocalBlogPosts(JSON.parse(savedPosts));
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlogPosts();
    // This effect should only run once on component mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generate categories dynamically from blog posts (for new post form only)
  const getUniqueCategories = () => {
    const uniqueCategories = Array.from(new Set(localBlogPosts.map(post => post.category)));
    return uniqueCategories.sort();
  };

  // Categories - dynamically generated from actual posts (for new post form only)
  const categories = getUniqueCategories();

  // Featured posts for the slider (newest 3 posts)
  const featuredPosts = [...localBlogPosts].sort((a, b) => b.id - a.id).slice(0, 3);

  // Navigation functions
  const nextSlide = () => {
    setCurrentSlide((prevSlide: number) => 
      prevSlide === featuredPosts.length - 1 ? 0 : prevSlide + 1
    );
  };
  
  const prevSlide = () => {
    setCurrentSlide((prevSlide: number) => 
      prevSlide === 0 ? featuredPosts.length - 1 : prevSlide - 1
    );
  };

  // Admin functions
  const handleAdminLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError("");
      
      // Use the auth service to login
      const result = await login(adminUsername, adminPassword);
      
      if (result.success) {
        setToken(result.token);
        localStorage.setItem('authToken', result.token);
        setIsAdmin(true);
        setShowLoginForm(false);
        setAdminPassword("");
      } else {
        // For demo fallback
        if (adminPassword === "admin123") {
          setIsAdmin(true);
          setShowLoginForm(false);
          setAdminPassword("");
        } else {
          setError(result.message || "Invalid credentials");
        }
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAdmin(false);
    setShowNewPostForm(false);
    setToken("");
    localStorage.removeItem('authToken');
  };

  const handleNewPostChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewPost((prev: BlogPost) => ({
      ...prev,
      [name]: name === "content" ? [value] : value
    }));
  };

  const handleSubmitNewPost = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError("");
      
      // Ensure the new post has a unique ID
      const updatedNewPost = {
        ...newPost,
        id: Date.now(), // Use timestamp for unique ID
        date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      };
      
      // Try to submit to API first
      if (token) {
        try {
          const response = await fetch(`${API_URL}/blog-posts`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(updatedNewPost)
          });
          
          if (response.ok) {
            const responseData = await response.json();
            const updatedPosts = [responseData, ...localBlogPosts];
            setLocalBlogPosts(updatedPosts);
            localStorage.setItem('blogPosts', JSON.stringify(updatedPosts));
          } else {
            throw new Error('Failed to submit post to API');
          }
        } catch (apiError) {
          console.error("API submission failed, saving locally:", apiError);
          // Update local state regardless of API success
          const updatedPosts = [updatedNewPost, ...localBlogPosts];
          setLocalBlogPosts(updatedPosts);
          localStorage.setItem('blogPosts', JSON.stringify(updatedPosts));
        }
      } else {
        // Local storage only
        const updatedPosts = [updatedNewPost, ...localBlogPosts];
        setLocalBlogPosts(updatedPosts);
        localStorage.setItem('blogPosts', JSON.stringify(updatedPosts));
      }
      
      // Reset form and close it
      setNewPost({
        id: Date.now() + 1, // Ensure next post also has unique ID
        title: "",
        excerpt: "",
        image: "https://picsum.photos/id/28/800/500",
        date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
        author: "Admin",
        category: "Career Advice",
        tags: ["New", "Recent"],
        content: ["Write your content here..."]
      });
      
      setShowNewPostForm(false);
    } catch (err) {
      console.error("Error submitting post:", err);
      setError("Failed to submit post. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Add a new function to handle post deletion
  const handleDeletePost = async (postId: number) => {
    try {
      setIsLoading(true);
      setError("");
      
      // Try to delete from API first
      if (token) {
        try {
          const response = await fetch(`${API_URL}/blog-posts/${postId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            // Update local state after successful API deletion
            setLocalBlogPosts(prevPosts => prevPosts.filter(post => post.id !== postId));
          } else {
            throw new Error('Failed to delete post from API');
          }
        } catch (apiError) {
          console.error("API deletion failed, proceeding with local deletion:", apiError);
          // Delete locally if API fails
          setLocalBlogPosts(prevPosts => prevPosts.filter(post => post.id !== postId));
          localStorage.setItem('blogPosts', JSON.stringify(
            localBlogPosts.filter(post => post.id !== postId)
          ));
        }
      } else {
        // Local deletion only
        setLocalBlogPosts(prevPosts => prevPosts.filter(post => post.id !== postId));
        localStorage.setItem('blogPosts', JSON.stringify(
          localBlogPosts.filter(post => post.id !== postId)
        ));
      }
    } catch (err) {
      console.error("Error deleting post:", err);
      setError("Failed to delete post. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Improve the Load More functionality
  const loadMorePosts = () => {
    setVisiblePosts(prevCount => prevCount + 6);
  };

  // Improved useEffect for loading posts with better error handling
  useEffect(() => {
    const loadBlogPosts = async () => {
      try {
        setIsLoading(true);
        setError(""); // Clear any previous errors

        // First try to load from localStorage
        const savedPosts = localStorage.getItem('blogPosts');
        if (savedPosts) {
          try {
            const parsedPosts = JSON.parse(savedPosts);
            if (Array.isArray(parsedPosts) && parsedPosts.length > 0) {
              setLocalBlogPosts(parsedPosts);
              setIsLoading(false);
              return; // Exit if we successfully loaded from localStorage
            }
          } catch (parseError) {
            console.error("Error parsing saved posts:", parseError);
            // Continue to API or default data if localStorage parsing fails
          }
        }

        // Try to fetch from API if localStorage didn't work
        try {
          const response = await fetch(`${API_URL}/blog-posts`);
          
          if (response.ok) {
            const data = await response.json();
            if (Array.isArray(data) && data.length > 0) {
              setLocalBlogPosts(data);
              localStorage.setItem('blogPosts', JSON.stringify(data));
              setIsLoading(false);
              return; // Exit if API call was successful
            }
          }
          throw new Error('API returned no data or invalid format');
        } catch (apiError) {
          console.error("API fetch failed:", apiError);
          // Fall back to default data if API fails
        }

        // If we get here, both localStorage and API failed, use default data
        console.log("Using default blog data");
        setLocalBlogPosts([...blogPostsData]);
        localStorage.setItem('blogPosts', JSON.stringify(blogPostsData));
        
        // Important: Don't set error here since we're falling back to default data
      } catch (err) {
        console.error("Error in blog post loading:", err);
        // Only set error if we couldn't load from any source
        if (localBlogPosts.length === 0) {
          setError("Failed to load blog posts");
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadBlogPosts();
  }, []);

  // Reset visible posts count
  React.useEffect(() => {
    setVisiblePosts(6);
  }, []);

  // Handle forgot password submission
  const handleForgotPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError("");
      
      // If server is known to be offline, show appropriate message
      if (serverConnected === false) {
        console.log("Server is known to be offline");
        setResetSuccess(true);
        setIsLoading(false);
        return;
      }
      
      // Send password reset request to API
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
        
        const response = await fetch(`${API_URL}/auth/reset-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: resetEmail
          }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          setResetSuccess(true);
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to reset password');
        }
      } catch (apiError: any) {
        console.error("API error:", apiError);
        
        // Update server connection status
        if (apiError.name === 'AbortError' || 
            (apiError instanceof TypeError && apiError.message === 'Failed to fetch')) {
          setServerConnected(false);
          console.log("Server connection failed");
          
          // In demo mode, proceed with reset password flow even when server is offline
          setResetSuccess(true);
          console.log("Demo mode: Password reset email would be sent to:", resetEmail);
        } else {
          // Re-throw other errors
          throw apiError;
        }
      }
    } catch (err) {
      console.error("Password reset error:", err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unexpected error occurred");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Close all forms
  const closeAllForms = () => {
    setShowLoginForm(false);
    setShowForgotPasswordForm(false);
    setResetSuccess(false);
    setError("");
  };

  // Check server connection status
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const isConnected = await checkServerStatus();
        setServerConnected(isConnected);
      } catch (error) {
        console.warn('Server connection check failed:', error);
        setServerConnected(false);
      }
    };
    
    checkConnection();
    
    // Periodically check connection
    const intervalId = setInterval(checkConnection, 30000);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="pt-20">
      {/* Admin Controls - Only visible when logged in */}
      <div className="fixed top-24 right-6 z-50">
        {isAdmin && (
          <div className="flex flex-col gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowNewPostForm(true)}
              className="bg-primary text-white p-2 rounded-full shadow-lg"
              title="Create New Post"
            >
              <FontAwesomeIcon icon={faPlus} />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleLogout}
              className="bg-red-500 text-white p-2 rounded-full shadow-lg"
              title="Logout"
            >
              <FontAwesomeIcon icon={faTimes} />
            </motion.button>
          </div>
        )}
      </div>

      {/* Admin Login Form - Redesigned */}
      {showLoginForm && !showForgotPasswordForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
          >
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800">Admin Portal</h2>
              <p className="text-gray-600 mt-2">Sign in to manage blog content</p>
            </div>
            
            <button 
              onClick={() => setShowLoginForm(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </button>
            
            {error && error !== "Failed to load blog posts" && (
              <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">{error}</p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleAdminLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faUser} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={adminUsername}
                    onChange={(e) => setAdminUsername(e.target.value)}
                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faKey} className="text-gray-400" />
                  </div>
                  <input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">Default: "admin123" (for demo only)</p>
              </div>
              
              <div>
                <button 
                  type="submit" 
                  className="w-full bg-primary text-white py-3 px-4 rounded-lg hover:bg-primary-dark transition-colors flex items-center justify-center"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Signing in...
                    </>
                  ) : (
                    <>Sign in</>
                  )}
                </button>
              </div>

              <div className="text-center mt-4">
                <button 
                  type="button" 
                  onClick={() => setShowForgotPasswordForm(true)}
                  className="text-primary hover:text-primary-dark text-sm"
                >
                  Forgot your password?
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Use the new ResetPassword component */}
      {showForgotPasswordForm && (
        <ResetPassword 
          onClose={closeAllForms} 
          defaultEmail={resetEmail} 
        />
      )}

      {/* New Post Form with Fixed Height */}
      {showNewPostForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-4" 
            style={{ maxHeight: 'calc(100vh - 40px)' }}
          >
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold">Create New Blog Post</h2>
              <button 
                type="button"
                onClick={() => setShowNewPostForm(false)} 
                className="text-gray-500 hover:text-gray-700"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <form onSubmit={handleSubmitNewPost} className="flex flex-col" style={{ maxHeight: 'calc(100vh - 140px)' }}>
              <div className="overflow-y-auto p-4 space-y-4">
                <div>
                  <label className="block text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={newPost.title}
                    onChange={handleNewPostChange}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-1">Excerpt</label>
                  <input
                    type="text"
                    name="excerpt"
                    value={newPost.excerpt}
                    onChange={handleNewPostChange}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-1">Image URL</label>
                  <input
                    type="text"
                    name="image"
                    value={newPost.image}
                    onChange={handleNewPostChange}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 mb-1">Author</label>
                    <input
                      type="text"
                      name="author"
                      value={newPost.author}
                      onChange={handleNewPostChange}
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-1">Category</label>
                    <select
                      name="category"
                      value={newPost.category}
                      onChange={handleNewPostChange}
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    >
                      {categories.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-gray-700 mb-1">Content</label>
                  <textarea
                    name="content"
                    value={newPost.content[0]}
                    onChange={handleNewPostChange}
                    rows={5}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  ></textarea>
                </div>
              </div>
                
              <div className="border-t p-4 flex justify-between sticky bottom-0 bg-white z-10">
                <button 
                  type="button" 
                  onClick={() => setShowNewPostForm(false)}
                  className="bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="bg-primary text-white py-2 px-6 rounded hover:bg-primary-dark transition-colors"
                >
                  Publish Blog Post
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Hero Section with Slider */}
      <section className="relative py-16 bg-gradient-to-r from-primary/10 to-secondary/10">
        <div className="container">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="max-w-4xl mx-auto text-center mb-12"
          >
            <h1 className="section-title">Our Blog</h1>
            <p className="text-lg md:text-xl text-gray-700 mt-4">
              Insights, trends, and guidance from the staffing and recruitment experts
            </p>
          </motion.div>

          {/* Featured Posts Slider */}
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ 
                  type: "spring", 
                  stiffness: 100, 
                  damping: 20 
                }}
                className="rounded-xl overflow-hidden shadow-xl"
              >
                <div className="relative">
                  <img 
                    src={featuredPosts[currentSlide].image} 
                    alt={featuredPosts[currentSlide].title}
                    className="w-full h-[500px] object-cover brightness-[0.85] contrast-[0.9]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end text-white p-8">
                    <div className="mb-2">
                      <span className="px-4 py-1 bg-primary rounded-full text-sm font-medium">
                        {featuredPosts[currentSlide].category}
                      </span>
                    </div>
                    <h2 className="text-2xl md:text-4xl font-bold mb-3 bg-black/40 backdrop-blur-sm p-3 rounded-lg inline-block">
                      {featuredPosts[currentSlide].title}
                    </h2>
                    <p className="text-lg md:text-xl mb-4 text-white/80">
                      {featuredPosts[currentSlide].excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm">
                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                        {featuredPosts[currentSlide].date}
                        <span className="mx-2">•</span>
                        <FontAwesomeIcon icon={faUser} className="mr-2" />
                        {featuredPosts[currentSlide].author}
                      </div>
                      <Link 
                        to={`/blog/${featuredPosts[currentSlide].id}`}
                        className="inline-flex items-center text-white hover:text-primary transition-colors"
                      >
                        Read more
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2 text-sm" />
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Slider Controls */}
            <div className="absolute top-1/2 -translate-y-1/2 left-4">
              <button 
                onClick={prevSlide} 
                className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-all"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
            </div>
            <div className="absolute top-1/2 -translate-y-1/2 right-4">
              <button 
                onClick={nextSlide} 
                className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-all"
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center mt-4">
              {featuredPosts.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full mx-1 transition-all ${
                    currentSlide === index ? "bg-primary w-6" : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="section">
        <div className="container">
          {error && error === "Failed to load blog posts" && localBlogPosts.length === 0 && (
            <div className="p-4 mb-6 bg-red-100 text-red-700 rounded-lg">
              {error}
            </div>
          )}
          
          {isLoading && !localBlogPosts.length ? (
            <div className="text-center py-12">
              <h3 className="text-xl font-medium text-gray-600">
                Loading blog posts...
              </h3>
            </div>
          ) : localBlogPosts.length > 0 ? (
            <>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={staggerContainer}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {localBlogPosts.slice(0, visiblePosts).map((post: BlogPost) => (
                  <motion.article
                    key={post.id}
                    variants={fadeInUp}
                    whileHover={{ y: -10, transition: { duration: 0.3 } }}
                    className="card overflow-hidden hover:shadow-xl transition-all duration-300 relative"
                  >
                    {isAdmin && (
                      <button 
                        onClick={() => handleDeletePost(post.id)}
                        className="absolute top-2 right-2 z-10 bg-red-500 text-white p-1 rounded-full shadow-lg w-8 h-8 flex items-center justify-center"
                        title="Delete Post"
                      >
                        <FontAwesomeIcon icon={faTimes} size="sm" />
                      </button>
                    )}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={post.image} 
                        alt={post.title} 
                        className="w-full h-full object-cover transition-all duration-500 hover:scale-110 brightness-[0.85] contrast-[0.9]"
                      />
                      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t from-black/70 to-transparent"></div>
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-primary/90 text-white text-xs font-medium rounded-full">
                          {post.category}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 w-full">
                        <h3 className="text-lg font-bold px-4 py-2 bg-black/40 backdrop-blur-sm text-white">
                          <Link to={`/blog/${post.id}`}>{post.title}</Link>
                        </h3>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center text-xs text-gray-500 mb-3">
                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                        <span>{post.date}</span>
                        <span className="mx-2">•</span>
                        <FontAwesomeIcon icon={faUser} className="mr-1" />
                        <span>{post.author}</span>
                      </div>
                      <p className="text-gray-600 mb-4">{post.excerpt}</p>
                      <Link 
                        to={`/blog/${post.id}`}
                        className="inline-flex items-center font-medium text-primary hover:text-primary-dark transition-colors"
                      >
                        Read more
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2 text-sm" />
                      </Link>
                    </div>
                  </motion.article>
                ))}
              </motion.div>

              {/* Load More Button - only shown if there are more posts to load */}
              {localBlogPosts.length > visiblePosts && (
                <div className="flex justify-center mt-12">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={loadMorePosts}
                    className="btn-teal-outline py-3 px-6"
                  >
                    Load More Articles
                  </motion.button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-xl font-medium text-gray-600">
                No posts found.
              </h3>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="section py-20 bg-gradient-to-r from-[#B01C1C] via-[#483D8B] to-[#00A7D1] text-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-3xl font-bold mb-4 text-white"
            >
              Subscribe to Our Newsletter
            </motion.h2>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="text-lg text-white/90 mb-8"
            >
              Stay updated with the latest industry insights, trends, and career opportunities.
            </motion.p>
            <motion.form
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4"
            >
              <input
                type="email"
                placeholder="Your email address"
                className="flex-grow px-6 py-4 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
              <button type="submit" className="btn bg-white text-primary hover:bg-gray-100 sm:w-auto px-8 py-4 rounded-full font-medium">
                Subscribe
              </button>
            </motion.form>
          </div>
        </div>

        {/* Login button in footer */}
        <div className="text-center mt-12">
          {!isAdmin && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowLoginForm(true)}
              className="text-white/70 hover:text-white text-sm underline"
            >
              Login
            </motion.button>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog; 